import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Table,
  TableColumn,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  Box,
  Button,
  Checkbox,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  makeStyles,
  Drawer,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';
import FilterListIcon from '@material-ui/icons/FilterList';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import InfoIcon from '@material-ui/icons/Info';
import { Issue, IssueStatus, PaginationInfo } from '../../types';
import { useIssueActions } from '../../hooks/useIssueActions';
import { IssueFilterAndSearch } from './IssueFilterAndSearch';
import { BulkStatusUpdateDialog } from './BulkStatusUpdateDialog';

const useStyles = makeStyles(theme => ({
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  chip: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: 2,
    boxShadow: 'none',
  },
  selectedRow: {
    backgroundColor: theme.palette.action.selected,
  },
  normalRow: {
    backgroundColor: theme.palette.background.paper,
  },
  table: {
    '& .MuiTableCell-root': {
      padding: theme.spacing(1, 2),
      borderRadius: 0,
    },
    '& .MuiPaper-root': {
      boxShadow: 'none',
      borderRadius: 0,
    },
    '& .MuiButton-root': {
      borderRadius: 2,
      boxShadow: 'none',
    },
  },
  badge: {
    marginLeft: theme.spacing(1),
  },
  activeFiltersButton: {
    marginRight: theme.spacing(1),
    backgroundColor: theme.palette.primary.light,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  issueTitle: {
    fontWeight: 500,
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  newIssueTitle: {
    fontWeight: 800,
    color: theme.palette.text.primary,
  },
  projectInfo: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginTop: theme.spacing(0.5),
  },
  newBadge: {
    marginLeft: theme.spacing(1),
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  eventCount: {
    fontWeight: 'bold',
    textAlign: 'center',
  },
  statusChip: {
    fontWeight: 'bold',
  },
  severityIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: '1rem',
  },
  bulkActions: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

type IssuesListComponentProps = {
  issues: Issue[];
  isLoading: boolean;
  error: Error | undefined;
  currentFilters: Record<string, string>;
  pagination?: PaginationInfo;
  onFilterChange: (filters: Record<string, string>) => void;
  onSearch: (query: string) => void;
  onRefresh: () => void;
  onIssueClick: (issue: Issue) => void;
  onPageChange: (cursor: string | undefined) => void;
  host: string;
  org: string;
  projectId: string;
};

export const IssuesListComponent = ({
  issues,
  isLoading,
  error,
  currentFilters,
  pagination,
  onFilterChange,
  onSearch,
  onRefresh,
  onIssueClick,
  onPageChange,
  host,
  org,
  projectId,
}: IssuesListComponentProps) => {
  const classes = useStyles();
  const [selectedIssues, setSelectedIssues] = useState<Set<number>>(new Set());
  const [isDrawerFilterOpen, setIsDrawerFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [highlightedIssues, setHighlightedIssues] = useState<Set<string>>(new Set());
  const seenIssuesRef = useRef<Record<string, string>>({});
  const hasBaselineRef = useRef(false);
  const storageKey = useMemo(
    () => (projectId ? `uptime-monitor-seen-issues-${projectId}` : 'uptime-monitor-seen-issues'),
    [projectId],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const stored = window.localStorage.getItem(storageKey);
      if (stored) {
        const parsed: Record<string, string> = JSON.parse(stored);
        seenIssuesRef.current = parsed;
        hasBaselineRef.current = Object.keys(parsed).length > 0;
      }
    } catch (err) {
      console.warn('Failed to restore seen issues from storage', err);
    }
  }, [storageKey]);

  useEffect(() => {
    if (!issues || issues.length === 0) {
      setHighlightedIssues(new Set<string>());
      return;
    }

    const nextSeen: Record<string, string> = { ...seenIssuesRef.current };
    const nextHighlighted = new Set<string>();

    issues.forEach(issue => {
      const id = issue.id;
      if (!id) {
        return;
      }
      const lastSeen = issue.lastSeen;
      const previousLastSeen = nextSeen[id];

      if (hasBaselineRef.current && (!previousLastSeen || previousLastSeen !== lastSeen)) {
        nextHighlighted.add(id);
      }

      nextSeen[id] = lastSeen;
    });

    seenIssuesRef.current = nextSeen;

    if (!hasBaselineRef.current) {
      hasBaselineRef.current = true;
      setHighlightedIssues(new Set<string>());
    } else {
      setHighlightedIssues(nextHighlighted);
    }

    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(nextSeen));
      } catch (err) {
        console.warn('Failed to persist seen issues to storage', err);
      }
    }
  }, [issues, storageKey]);

  const markIssueAsViewed = useCallback(
    (issue: Issue) => {
      if (!issue || !issue.id) {
        return;
      }

      setHighlightedIssues(prev => {
        if (!prev.has(issue.id)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(issue.id);
        return next;
      });

      seenIssuesRef.current[issue.id] = issue.lastSeen;

      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(seenIssuesRef.current));
        } catch (err) {
          console.warn('Failed to persist seen issues to storage', err);
        }
      }
    },
    [storageKey],
  );

  const handleIssueSelect = useCallback(
    (issue: Issue) => {
      if (!issue) {
        return;
      }
      markIssueAsViewed(issue);
      onIssueClick(issue);
    },
    [markIssueAsViewed, onIssueClick],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const interval = window.setInterval(() => {
      onRefresh();
    }, 30000);

    return () => {
      window.clearInterval(interval);
    };
  }, [onRefresh]);
  
  const {
    markAsResolvedWithComment,
    markAsIgnoredWithComment, 
    markAsUnresolvedWithComment,
    isUpdating 
  } = useIssueActions();

  const [bulkStatusDialog, setBulkStatusDialog] = useState<{
    open: boolean;
    status: IssueStatus | null;
  }>({ open: false, status: null });

  const hasActiveFilters = useMemo(() => {
    return Object.entries(currentFilters).some(([key, value]) => {
      if (key === 'limit' || key === 'sort') return false;
      return value && value !== '';
    });
  }, [currentFilters]);

  const pageSize = parseInt(currentFilters.limit || '10');

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedIssues(new Set(issues.map(issue => parseInt(issue.id))));
    } else {
      setSelectedIssues(new Set());
    }
  }, [issues]);

  const handleSelectIssue = useCallback((issueId: number, checked: boolean) => {
    setSelectedIssues(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(issueId);
      } else {
        newSet.delete(issueId);
      }
      return newSet;
    });
  }, []);

  const handleBulkActionClick = useCallback((action: IssueStatus) => {
    if (selectedIssues.size === 0) return;
    setBulkStatusDialog({ open: true, status: action });
  }, [selectedIssues.size]);
  
  const handleBulkStatusClose = () => {
    setBulkStatusDialog({ open: false, status: null });
  };
  
  const handleBulkStatusConfirm = useCallback(async (comment: string) => {
    const issueIds = Array.from(selectedIssues);
    if (issueIds.length === 0) return;
  
    const action = bulkStatusDialog.status;
    if (!action) return;
  
    try {
      // Process each issue individually to include comments
      for (const issueId of issueIds) {
        switch (action) {
          case IssueStatus.RESOLVED:
            await markAsResolvedWithComment(host, org, issueId, comment);
            break;
          case IssueStatus.IGNORED:
            await markAsIgnoredWithComment(host, org, issueId, comment);
            break;
          case IssueStatus.UNRESOLVED:
            await markAsUnresolvedWithComment(host, org, issueId, comment);
            break;
        }
      }
      
      setBulkStatusDialog({ open: false, status: null });
      setSelectedIssues(new Set());
      onRefresh();
      
      console.log(`Bulk ${action} completed for ${issueIds.length} issues with comment: ${comment}`);
    } catch (err) {
      console.error('Failed to update issue status:', err);
      setBulkStatusDialog({ open: false, status: null });
    }
  }, [selectedIssues, bulkStatusDialog.status, host, org, markAsResolvedWithComment, markAsIgnoredWithComment, markAsUnresolvedWithComment, onRefresh]);

  const toggleDrawerFilter = (open: boolean) => {
    setIsDrawerFilterOpen(open);
  };

  // Handle table pagination change
  const handleTablePageChange = useCallback((page: number) => {
    if (page > currentPage) {
      // Going to next page
      if (pagination?.next) {
        try {
          const url = new URL(pagination.next);
          const cursor = url.searchParams.get('cursor');
          setCurrentPage(page);
          onPageChange(cursor || undefined);
        } catch (error) {
          console.error('Error parsing next URL:', error);
        }
      }
    } else if (page < currentPage) {
      // Going to previous page
      if (pagination?.previous) {
        try {
          const url = new URL(pagination.previous);
          const cursor = url.searchParams.get('cursor');
          setCurrentPage(page);
          onPageChange(cursor || undefined);
        } catch (error) {
          console.error('Error parsing previous URL:', error);
        }
      } else if (page === 0) {
        // Going back to first page
        setCurrentPage(0);
        onPageChange(undefined);
      }
    }
  }, [currentPage, pagination, onPageChange]);

  const handleFilterChangeWithReset = useCallback((newFilters: Record<string, string>) => {
    setCurrentPage(0);
    onFilterChange(newFilters);
  }, [onFilterChange]);

  const handleSearchWithReset = useCallback((query: string) => {
    setCurrentPage(0);
    onSearch(query);
  }, [onSearch]);

  const getSeverityIcon = (level: string) => {
    switch (level?.toLowerCase()) {
      case 'error':
        return <ErrorIcon className={classes.severityIcon} style={{ color: '#d73a49' }} />;
      case 'warning':
        return <WarningIcon className={classes.severityIcon} style={{ color: '#f66a0a' }} />;
      default:
        return <InfoIcon className={classes.severityIcon} style={{ color: '#0969da' }} />;
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      resolved: { 
        color: '#28a745', 
        backgroundColor: '#d4edda',
        label: 'Resolved'
      },
      unresolved: { 
        color: '#dc3545', 
        backgroundColor: '#f8d7da',
        label: 'Unresolved'
      },
      ignored: { 
        color: '#6c757d', 
        backgroundColor: '#f8f9fa',
        label: 'Ignored'
      },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.unresolved;

    return (
      <Chip
        label={config.label}
        size="small"
        className={classes.statusChip}
        style={{ 
          color: config.color, 
          backgroundColor: config.backgroundColor,
          border: `1px solid ${config.color}20`
        }}
      />
    );
  };

  const columns: TableColumn<Issue>[] = [
    {
      title: (
        <Checkbox
          checked={selectedIssues.size === issues.length && issues.length > 0}
          indeterminate={selectedIssues.size > 0 && selectedIssues.size < issues.length}
          onChange={(e) => handleSelectAll(e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      width: '5%',
      render: (issue) => (
        <Checkbox
          checked={selectedIssues.has(parseInt(issue.id))}
          onChange={(e) => handleSelectIssue(parseInt(issue.id), e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
    },
    {
      title: 'ID',
      width: '3%',
      render: (issue) => (
        <Typography>
          {parseInt(issue.id).toLocaleString()}
        </Typography>
      ),
    },
    {
      title: 'Issue',
      width: '50%',
      render: (issue) => (
        <Box>
          <Box display="flex" alignItems="center">
            {getSeverityIcon(issue.level)}
            <Typography
              className={`${classes.issueTitle} ${highlightedIssues.has(issue.id) ? classes.newIssueTitle : ''}`}
              onClick={() => handleIssueSelect(issue)}
            >
              {issue.title}
            </Typography>
            {highlightedIssues.has(issue.id) && (
              <Chip
                label="New"
                size="small"
                color="secondary"
                className={classes.newBadge}
              />
            )}
          </Box>
          <Typography className={classes.projectInfo}>
            {issue.project.name} — {new Date(issue.firstSeen).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: false,
            })}
          </Typography>
        </Box>
      ),
    },
    {
      title: 'Status',
      width: '10%',
      render: (issue) => getStatusChip(issue.status),
    },
    {
      title: 'Events',
      width: '5%',
      render: (issue) => (
        <Typography className={classes.eventCount}>
          {parseInt(issue.count).toLocaleString()}
        </Typography>
      ),
    },
    {
      title: 'Last Seen',
      width: '15%',
      render: (issue) => (
        new Date(issue.lastSeen).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
          timeZoneName: 'longOffset'
        })
      ),
    },
  ];

  if (isLoading) {
    return <Progress />;
  }

  if (error) {
    return <ResponseErrorPanel error={error} />;
  }

  return (
    <Box>
      {/* Bulk Actions */}
      {selectedIssues.size > 0 && (
        <Box className={classes.bulkActions}>
          <Typography variant="body2" style={{ marginRight: 16 }}>
            {selectedIssues.size} selected
          </Typography>
          <Button
            size="small"
            startIcon={<CheckIcon />}
            onClick={() => handleBulkActionClick(IssueStatus.RESOLVED)}
            disabled={isUpdating}
            variant="contained"
            color="primary"
          >
            Resolve
          </Button>
          <Button
            size="small"
            startIcon={<CloseIcon />}
            onClick={() => handleBulkActionClick(IssueStatus.UNRESOLVED)}
            disabled={isUpdating}
            variant="outlined"
          >
            Reopen
          </Button>
          <Button
            size="small"
            startIcon={<VisibilityOffIcon />}
            onClick={() => handleBulkActionClick(IssueStatus.IGNORED)}
            disabled={isUpdating}
            variant="outlined"
          >
            Ignore
          </Button>
        </Box>
      )}

      {/* Bulk Status Update Dialog */}
      <BulkStatusUpdateDialog
        open={bulkStatusDialog.open}
        status={bulkStatusDialog.status}
        issueCount={selectedIssues.size}
        isUpdating={isUpdating}
        onClose={handleBulkStatusClose}
        onConfirm={handleBulkStatusConfirm}
      />

      {/* Errors Table */}
      <Table
        title="Error list"
        options={{
          search: false,
          paging: true,
          exportAllData: false,
          paginationType: 'normal',
          paginationPosition: 'bottom',
          sorting: false,
          filtering: false,
          pageSize: pageSize,
          pageSizeOptions: [10, 20, 50, 100],
          emptyRowsWhenPaging: false,
        }}
        columns={columns}
        data={issues}
        page={currentPage}
        onPageChange={handleTablePageChange}
        onRowClick={(event, issue) => {
          const target = event?.target as HTMLElement;
          if (target?.closest('input[type="checkbox"]') || target?.closest('.MuiCheckbox-root')) {
            return;
          }
          handleIssueSelect(issue as Issue);
        }}
        components={{
          Toolbar: props => (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              py={1}
              px={2}
            >
              <Box>{props.title}</Box>
              <Box display="flex" alignItems="center">
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={hasActiveFilters ? <FilterListIcon /> : <SearchIcon />}
                  onClick={() => toggleDrawerFilter(true)}
                  className={
                    hasActiveFilters ? classes.activeFiltersButton : undefined
                  }
                >
                  {hasActiveFilters ? 'Filter Applied' : 'Filter'}
                </Button>
                <Tooltip title="Refresh">
                  <IconButton 
                    onClick={onRefresh} 
                    color="primary"
                    style={{ marginLeft: 8 }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ),
        }}
      />

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={isDrawerFilterOpen}
        onClose={(e: any) => {
          e.stopPropagation();
          toggleDrawerFilter(false);
        }}
        onClick={event => event.stopPropagation()}
        style={{ zIndex: 1300 }} 
        PaperProps={{
          style: { zIndex: 1300 }
        }}
        ModalProps={{
          disableEnforceFocus: false, 
          disableAutoFocus: false,    
        }}
      >
        <IssueFilterAndSearch
          onClose={() => toggleDrawerFilter(false)}
          currentFilters={currentFilters}
          onApplyFilters={(newFilters) => {
            handleFilterChangeWithReset(newFilters);
            toggleDrawerFilter(false);
          }}
          onSearch={handleSearchWithReset}
        />
      </Drawer>
    </Box>
  );
};
